import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

import {
  Table,
  Button,
  Row,
  Divider,
  Typography,
  Tag,
  Space,
  Tooltip,
} from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { SendOutlined, FilterOutlined } from '@ant-design/icons';

import Breadcrumb from '../../../../components/common/breadcrumb';
import Layout from '../../../../components/layout';
import { statusTone, toneStyle } from '../../utils/statusPalette';
import { Candidate, Status } from '../../../../fsm/jobs/jobs.fsm.types';
import styles from './index.module.css';

import ShareModal from '../../components/share-modal';

import { usePermissions } from '../../../../context/permission.context';
import { User } from '../../../../fsm/users';
import Filters from '../../components/filters';

const { Text } = Typography;

const orderMapping = {
  ascend: 'ASC',
  descend: 'DESC',
};

const STATUS_LABEL = {
  NEW: 'New',
  OPEN: 'Open',
  REVIEWED: 'Reviewed',
  INVITATION_SENT: 'Invitation sent',
  RIO_SCREENING: 'Rio screening',
  ALT_INTERVIEW: 'Alt interview',
  HM_REVIEW: 'HM review',
  REJECTED: 'Rejected',
  HIRED: 'Hired',
  NOT_INTERESTED: 'Not interested',
};

type TableRowSelection<T extends object = object> =
  TableProps<T>['rowSelection'];

interface Props {
  user?: User;
  data: Candidate[];
  isLoading: boolean;
  /** The jobs state machine is in its terminal error state. */
  failed: boolean;
  totalElements: number;
  totalPages: number;
  jobTitle?: string;
  onFetchCandidates: (
    jobId: number,
    pageNumber: number,
    pageSize: number,
    fitScore?: number,
    statuses?: Status[],
    hasMandatorySkills?: boolean,
    hasMandatoryTitleMatch?: boolean,
    maxDistance?: number,
    candidatesWithNoLocation?: boolean,
    isApplicant?: boolean,
    sort?: string,
    order?: string,
  ) => void;
  onSendCandidates: (
    jobId: number,
    candidateIds: React.Key[],
    emails: string[],
    emailBody: string,
  ) => void;
  onFetchJobDetails: (id: number) => void;
}

const breadcrumbItems = [
  {
    title: <Link to="/jobs">Job list</Link>,
  },
  {
    title: 'Candidates',
  },
];

const Candidates = ({
  user,
  data,
  isLoading,
  failed,
  totalElements,
  totalPages,
  jobTitle,
  onFetchCandidates,
  onSendCandidates,
  onFetchJobDetails,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [sortOrder] = useState<string | undefined>('DESC');
  const [sortField] = useState<string | undefined>('fitScore');
  const [pageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(isMobile ? 6 : 10);
  const { jobId } = useParams();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<{
    fitScore?: number;
    statuses?: Status[];
    hasMandatorySkills?: boolean;
    hasMandatoryTitleMatch?: boolean;
    maxDistance?: number;
    candidatesWithNoLocation?: boolean;
    isApplicant?: boolean;
  }>({
    fitScore: undefined,
    statuses: undefined,
    hasMandatorySkills: undefined,
    hasMandatoryTitleMatch: undefined,
    maxDistance: undefined,
    candidatesWithNoLocation: undefined,
    isApplicant: undefined,
  });
  const { canSeeCandidateSendEmailButton, canSeeCandidateActionColumn } =
    usePermissions();

  // Fetch candidates when jobId or filters change
  useEffect(() => {
    if (jobId) {
      onFetchCandidates(
        parseInt(jobId),
        pageNumber,
        pageSize,
        filters.fitScore,
        filters.statuses,
        filters.hasMandatorySkills,
        filters.hasMandatoryTitleMatch,
        filters.maxDistance,
        filters.candidatesWithNoLocation,
        filters.isApplicant,
        sortField,
        sortOrder,
      );
    }
  }, [
    jobId,
    filters,
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    onFetchCandidates,
  ]);

  // Fetch job details with a small delay to let candidates fetch first
  useEffect(() => {
    if (jobId) {
      const timer = setTimeout(() => {
        onFetchJobDetails(parseInt(jobId));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [jobId, onFetchJobDetails]);

  const list = useMemo(() => {
    return data.map((item) => ({ ...item, key: item.id }));
  }, [data]);

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      _: Record<string, any>,
      sorter: SorterResult<Candidate> | SorterResult<Candidate>[],
    ) => {
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

      jobId &&
        onFetchCandidates(
          parseInt(jobId),
          (pagination.current as number) || pageNumber,
          pageSize,
          filters.fitScore,
          filters.statuses,
          filters.hasMandatorySkills,
          filters.hasMandatoryTitleMatch,
          filters.maxDistance,
          filters.candidatesWithNoLocation,
          filters.isApplicant,
          currentSorter.field as string,
          orderMapping[currentSorter.order as keyof typeof orderMapping],
        );
    },
    [jobId, pageSize, filters, pageNumber, onFetchCandidates],
  );

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<Candidate> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const handleModalClose = useCallback(
    (emptySelectedRows?: boolean) => {
      setIsModalOpen(false);
      if (emptySelectedRows) {
        setSelectedRowKeys([]);
      }
    },
    [setIsModalOpen],
  );

  const handleFiltersOpen = useCallback(() => {
    setIsFiltersOpen(true);
  }, [setIsFiltersOpen]);

  const handleFiltersClose = useCallback(() => {
    setIsFiltersOpen(false);
  }, [setIsFiltersOpen]);

  const handleFiltersApply = useCallback(
    (filters: {
      fitScore?: number;
      statuses?: Status[];
      hasMandatorySkills?: boolean;
      hasMandatoryTitleMatch?: boolean;
      maxDistance?: number;
      candidatesWithNoLocation?: boolean;
      isApplicant?: boolean;
    }) => {
      setFilters(filters);
      handleFiltersClose();
    },
    [jobId, handleFiltersClose],
  );

  // Loading, a failed request and an active filter all leave the table empty
  // too, and none of them means the job has no candidates.
  const hasActiveFilters = Object.values(filters).some((value) =>
    Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && value !== false,
  );
  const emptyText = isLoading ? (
    <span />
  ) : failed ? (
    'Candidates could not be loaded. Refresh the page to try again.'
  ) : hasActiveFilters ? (
    'No candidates match the current filters.'
  ) : (
    'No candidates matched to this job yet.'
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row: Candidate) => (
        <Link to={canSeeCandidateActionColumn ? row.id.toString() : ''}>
          {name}
        </Link>
      ),
    },
    {
      title: 'Fitscore',
      dataIndex: 'fitScore',
      key: 'fitScore',
      render: (fitscore: number, item: Candidate) => (
        <Tooltip placement="top" title={item.skills.join(', ')}>
          <div className={styles.card}>
            {typeof fitscore === 'number' && Number.isFinite(fitscore) ? (
              <div className={styles.cardScore}>{fitscore}</div>
            ) : (
              // Not a zero, and not blank. Zero on a 0-100 scale reads as the
              // worst possible candidate; blank reads as a rendering fault. The
              // truth is that nobody scored them, which is what the scorecard
              // says too.
              <div className={styles.cardScoreUnscored}>Not scored</div>
            )}
            {typeof item.interviewAdjustedFitScore === 'number' &&
              Number.isFinite(item.interviewAdjustedFitScore) && (
                // Plain text, not a coloured up/down badge - this exact column
                // already had a tinted-chip treatment removed once because it
                // read as a status rather than a measurement (see this file's
                // .card comment). fitScore stays the matcher's score, and
                // sorting and the score filter run on it, so the interview's
                // effect is shown BESIDE it rather than replacing it: swapping
                // the number would leave the order and the filter disagreeing
                // with what the row shows. Driven by the list item's own
                // interviewAdjustedFitScore; the branch this replaces keyed on
                // item.rescore, which the list endpoint never returns, so it
                // could not fire against the real API.
                <Tooltip
                  placement="top"
                  title="Interview-adjusted score. The number above is the resume match, which sorting and the score filter use. Open the candidate for what changed."
                >
                  <div className={styles.cardInfo}>
                    After interview: {item.interviewAdjustedFitScore}
                  </div>
                </Tooltip>
              )}
          </div>
        </Tooltip>
      ),
      sorter: true,
    },
    {
      title: 'Company',
      dataIndex: 'employerName',
      key: 'employerName',
    },
    {
      title: 'Last position',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: string) => {
        const style = toneStyle(statusTone(status));
        return (
          <Tag style={{ ...style, fontWeight: 500 }}>
            {STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status}
          </Tag>
        );
      },
    },
    {
      title: 'Last activity',
      dataIndex: 'lastActivityAt',
      key: 'lastActivityAt',
      render: (postedAt?: number) => (
        <div className={styles.nowrap}>
          {postedAt
            ? new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }).format(new Date(postedAt * 1000))
            : 'No date'}
        </div>
      ),
      sorter: true,
    },
    ...(canSeeCandidateActionColumn
      ? [
          {
            title: 'Actions',
            dataIndex: 'listing',
            key: 'listing',
            render: (_a: unknown, { id }: Candidate) => (
              <Link className={styles.nowrap} to={id.toString()}>
                View profile
              </Link>
            ),
          },
        ]
      : []),
  ];

  return (
    <Layout user={user}>
      <Breadcrumb
        items={breadcrumbItems}
        renderRight={
          <Button type="primary" shape="round">
            <Link to={`/jobs/${jobId}`}>View job post</Link>
          </Button>
        }
      />
      <div className={styles.wrapper}>
        <Row
          align={'middle'}
          justify={'space-between'}
          style={{ padding: isMobile ? '8px 0px' : 8 }}
        >
          <div>
            <Text style={{ fontSize: 16 }}>
              {isMobile
                ? 'Candidates'
                : `Candidates for ${jobTitle || 'this role'}`}
            </Text>
            <Divider type="vertical" />
            <Text type="secondary" style={{ fontSize: 16 }}>
              Total {totalElements}
            </Text>
          </div>
          <div>
            <Space>
              {canSeeCandidateSendEmailButton && (
                <Button
                  type="text"
                  icon={isMobile ? undefined : <SendOutlined />}
                  disabled={!selectedRowKeys.length}
                  onClick={handleModalOpen}
                >
                  Share
                </Button>
              )}
              <Button icon={<FilterOutlined />} onClick={handleFiltersOpen}>
                Filters
              </Button>
            </Space>
          </div>
        </Row>
        <Table
          dataSource={list}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText }}
          scroll={{ x: 800 }}
          pagination={
            totalPages > 1 && {
              total: totalElements,
              pageSize: pageSize,
              showSizeChanger: false,
            }
          }
          rowSelection={
            canSeeCandidateSendEmailButton ? rowSelection : undefined
          }
          onChange={handleTableChange}
        />
      </div>
      <ShareModal
        isOpen={isModalOpen}
        isLoading={isLoading}
        candidateIds={selectedRowKeys}
        onClose={handleModalClose}
        onSendCandidates={onSendCandidates}
      />
      <Filters
        isOpen={isFiltersOpen}
        onClose={handleFiltersClose}
        onApply={handleFiltersApply}
      />
    </Layout>
  );
};

export default memo(Candidates);
